import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, User, Camera, Trash, PencilSimple, Bank, Bell, SignOut, FloppyDisk, X, Lock
} from '@phosphor-icons/react';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  const [bio, setBio] = useState(user?.bio || '');
  const [editingBio, setEditingBio] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState(user?.personal_name || user?.username || '');
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankData, setBankData] = useState({ bank_name: '', account_holder_name: '', bank_account_number: '', ifsc_code: '', upi_id: '', address: '' });
  const [savingBank, setSavingBank] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [selectedTune, setSelectedTune] = useState(() => {
    try { return localStorage.getItem('dws_customer_tune') || 'default'; } catch { return 'default'; }
  });
  const [tuneSaved, setTuneSaved] = useState(false);
  const notifInputRef = useRef(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);

  // P22 — draggable + resizable centered modal (was full-screen page).
  const dragRef = useRef(null);
  const [box, setBox] = useState(() => {
    const w = Math.min(520, window.innerWidth - 24);
    const h = Math.min(Math.round(window.innerHeight * 0.86), window.innerHeight - 24);
    return { x: Math.max(12, (window.innerWidth - w) / 2), y: Math.max(12, (window.innerHeight - h) / 2), w, h };
  });
  function onDragMove(e) {
    const d = dragRef.current; if (!d) return;
    if (e.cancelable) e.preventDefault();
    const pt = e.touches ? e.touches[0] : e;
    const dx = pt.clientX - d.sx, dy = pt.clientY - d.sy;
    if (d.mode === 'drag') {
      setBox((b) => ({ ...b, x: Math.min(Math.max(0, d.ox + dx), window.innerWidth - 60), y: Math.min(Math.max(0, d.oy + dy), window.innerHeight - 40) }));
    } else {
      setBox((b) => ({ ...b, w: Math.max(300, Math.min(d.ow + dx, window.innerWidth - b.x - 8)), h: Math.max(320, Math.min(d.oh + dy, window.innerHeight - b.y - 8)) }));
    }
  }
  function onDragEnd() {
    dragRef.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
    window.removeEventListener('touchmove', onDragMove);
    window.removeEventListener('touchend', onDragEnd);
  }
  function beginDrag(e, mode) {
    const pt = e.touches ? e.touches[0] : e;
    dragRef.current = { mode, sx: pt.clientX, sy: pt.clientY, ox: box.x, oy: box.y, ow: box.w, oh: box.h };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
  }

  const defaultTunes = [
    { id: 'default', name: 'Default' },
    { id: 'tone1', name: 'Classic Bell' },
    { id: 'tone2', name: 'Soft Chime' },
    { id: 'tone3', name: 'Digital Pop' },
    { id: 'tone4', name: 'Gentle Ping' },
    { id: 'none', name: 'None (Silent)' },
  ];

  // Fetch existing bank details. Point 23 — saved account details are
  // static identity data (not a live balance/transaction status), so this
  // paints instantly from cache and silently refreshes behind it.
  useEffect(() => {
    if (!user?.id) return;
    const cached = readInstantCache('customer_bank_details', user.id);
    if (cached) setBankData(cached);
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/client-bank-details/${user.id}`);
        if (res.data?.bank_details) {
          setBankData(res.data.bank_details);
          writeInstantCache('customer_bank_details', user.id, res.data.bank_details);
        }
      } catch (e) {}
    };
    fetch();
  }, [user]);

  const profilePicUrl = user?.profile_pic 
    ? (user.profile_pic.startsWith('http') ? user.profile_pic : `${API}${user.profile_pic}`)
    : null;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/customer/${user.id}/upload-profile`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser({ ...user, profile_pic: res.data.filename });
      toast.success('Profile picture updated!');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeletePic = async () => {
    try {
      await axios.delete(`${API}/customer/${user.id}/profile`);
      updateUser({ ...user, profile_pic: null });
      toast.success('Profile picture removed!');
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleSaveBio = async () => {
    try {
      await axios.post(`${API}/customer/${user.id}/update-bio`, { bio });
      updateUser({ ...user, bio });
      setEditingBio(false);
      toast.success('Bio updated!');
    } catch (e) { toast.error('Failed to save bio'); }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) { toast.error('Name cannot be empty'); return; }
    try {
      await axios.put(`${API}/users/customer/name?customer_id=${user.id}&name=${encodeURIComponent(editName.trim())}`);
      updateUser({ ...user, personal_name: editName.trim() });
      setEditingName(false);
      toast.success('Name updated!');
    } catch (e) { toast.error('Failed to update name'); }
  };

  const handleSaveBank = async () => {
    setSavingBank(true);
    try {
      await axios.post(`${API}/client-bank-details`, { customer_id: user.id, ...bankData });
      toast.success('Bank details saved!');
      setShowBankForm(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Failed to save');
    }
    finally { setSavingBank(false); }
  };

  const handleLogout = () => {
    logout();
    navigate('/customer-login', { replace: true });
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40" data-testid="customer-dashboard-backdrop">
      <div
        className="absolute bg-gray-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
        data-testid="customer-dashboard-modal"
      >
      {/* Header (drag handle) */}
      <div
        onMouseDown={(e) => beginDrag(e, 'drag')}
        onTouchStart={(e) => beginDrag(e, 'drag')}
        className="bg-[#008069] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0 cursor-move select-none"
      >
        <button onMouseDown={(e) => e.stopPropagation()} onClick={() => navigate('/customer-select')} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="dashboard-back-btn">
          <ArrowLeft size={22} weight="bold" />
        </button>
        <h1 className="text-lg font-semibold flex-1">Profile</h1>
        <button onMouseDown={(e) => e.stopPropagation()} onClick={() => navigate('/customer-select')} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="dashboard-close-btn">
          <X size={22} weight="bold" />
        </button>
      </div>

      {/* Profile Content */}
      <div className="flex-1 overflow-y-auto">
        {/* DP Section */}
        <div className="bg-white py-8 flex flex-col items-center">
          <div className="relative">
            {profilePicUrl ? (
              <img src={profilePicUrl} alt="Profile" className="w-28 h-28 rounded-full object-cover ring-4 ring-[#008069]/20" />
            ) : (
              <div className="w-28 h-28 rounded-full bg-[#dfe5e7] flex items-center justify-center ring-4 ring-[#008069]/20">
                <User size={56} className="text-[#a0aeb4]" />
              </div>
            )}
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="absolute bottom-0 right-0 w-10 h-10 bg-[#008069] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#006e5a] disabled:opacity-50">
              <Camera size={18} weight="fill" />
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
          
          {profilePicUrl && (
            <button onClick={handleDeletePic} className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
              <Trash size={12} /> Remove photo
            </button>
          )}

          <h2 className="text-xl font-bold text-gray-800 mt-3">{user?.personal_name || user?.username}</h2>
          <p className="text-sm text-gray-500">{user?.phone}</p>
          <button onClick={() => { setEditName(user?.personal_name || user?.username || ''); setEditingName(true); }} className="mt-1 text-xs text-[#008069] hover:underline flex items-center gap-1">
            <PencilSimple size={12} /> Edit Name
          </button>
        </div>

        {/* Name Edit Modal */}
        {editingName && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-5 w-full max-w-sm">
              <h3 className="text-lg font-semibold mb-3">Edit Name</h3>
              <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Enter your name"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]" maxLength={50} autoFocus data-testid="edit-name-input" />
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditingName(false)} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSaveName} className="flex-1 py-2 bg-[#008069] text-white rounded-lg text-sm hover:bg-[#006e5a]" data-testid="save-name-btn">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Bio Section */}
        <div className="bg-white mt-2 px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#008069] font-semibold uppercase tracking-wide">About</span>
            <button onClick={() => setEditingBio(!editingBio)} className="p-1 hover:bg-gray-100 rounded-full">
              <PencilSimple size={16} className="text-[#008069]" />
            </button>
          </div>
          {editingBio ? (
            <div className="flex gap-2">
              <input value={bio} onChange={e => setBio(e.target.value)} placeholder="Write something about yourself..."
                className="flex-1 text-sm border-b-2 border-[#008069] outline-none py-1 bg-transparent" maxLength={150} autoFocus />
              <button onClick={handleSaveBio} className="p-1.5 bg-[#008069] text-white rounded-full"><FloppyDisk size={16} /></button>
            </div>
          ) : (
            <p className="text-sm text-gray-700">{bio || 'Hey there! I am using DWS Vault Chat'}</p>
          )}
        </div>

        {/* Bank Account Section */}
        <div className="bg-white mt-2 px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Bank size={20} className="text-[#008069]" />
            <span className="text-xs text-[#008069] font-semibold uppercase tracking-wide">Bank Account</span>
          </div>
          
          {bankData.bank_name || bankData.bank_account_number ? (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm mb-3">
              {bankData.bank_name && <div className="flex justify-between"><span className="text-gray-500">Bank</span><span className="font-medium">{bankData.bank_name}</span></div>}
              {bankData.account_holder_name && <div className="flex justify-between"><span className="text-gray-500">Holder</span><span className="font-medium">{bankData.account_holder_name}</span></div>}
              {bankData.bank_account_number && <div className="flex justify-between"><span className="text-gray-500">A/c No</span><span className="font-medium">{bankData.bank_account_number}</span></div>}
              {bankData.ifsc_code && <div className="flex justify-between"><span className="text-gray-500">IFSC</span><span className="font-medium">{bankData.ifsc_code}</span></div>}
              {bankData.upi_id && <div className="flex justify-between"><span className="text-gray-500">UPI</span><span className="font-medium">{bankData.upi_id}</span></div>}
            </div>
          ) : null}

          <div className="space-y-2">
            {!bankData.bank_name && !bankData.bank_account_number ? (
              <button onClick={() => setShowBankForm(true)}
                className="w-full py-2.5 text-sm font-medium text-[#008069] bg-[#008069]/5 rounded-lg hover:bg-[#008069]/10 border border-[#008069]/20"
                data-testid="customer-save-bank-btn">
                Save New Account
              </button>
            ) : (
              <div
                className="w-full text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 flex items-start gap-2"
                data-testid="customer-bank-locked-note"
              >
                <span className="text-base leading-none">🔒</span>
                <span>
                  Bank details lock ho gayi hain. Change karne ke liye Support / Admin
                  se contact karein.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Notification Section */}
        <div className="bg-white mt-2 px-5 py-4">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowNotification(!showNotification)}>
            <div className="flex items-center gap-2">
              <Bell size={20} className="text-[#008069]" />
              <span className="text-xs text-[#008069] font-semibold uppercase tracking-wide">Notification</span>
            </div>
            <div className="flex items-center gap-2">
              {tuneSaved && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">Saved</span>}
              <span className="text-xs text-gray-500">{defaultTunes.find(t => t.id === selectedTune)?.name || 'Default'}</span>
            </div>
          </div>
          {showNotification && (
            <div className="mt-3 space-y-1">
              {defaultTunes.map(tune => (
                <div key={tune.id} className="flex items-center gap-2">
                  <button onClick={() => {
                    setSelectedTune(tune.id);
                    localStorage.setItem('dws_customer_tune', tune.id);
                    setTuneSaved(true);
                    setTimeout(() => setTuneSaved(false), 3000);
                    toast.success(`Notification tune: ${tune.name}`);
                  }}
                    className={`flex-1 text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${selectedTune === tune.id ? 'bg-[#008069]/10 text-[#008069] font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {selectedTune === tune.id && <span className="text-green-600 text-lg">&#10003;</span>}
                    {tune.name}
                  </button>
                  <button onClick={() => {
                    try {
                      const ctx = new (window.AudioContext || window.webkitAudioContext)();
                      const freqMap = { default: 440, tone1: 523, tone2: 392, tone3: 587, tone4: 330 };
                      const osc = ctx.createOscillator();
                      const gain = ctx.createGain();
                      osc.connect(gain);
                      gain.connect(ctx.destination);
                      osc.frequency.value = freqMap[tune.id] || 440;
                      osc.type = tune.id === 'tone3' ? 'square' : tune.id === 'tone2' ? 'sine' : 'triangle';
                      gain.gain.value = 0.2;
                      osc.start();
                      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
                      osc.stop(ctx.currentTime + 0.8);
                    } catch(e) {}
                  }} className="px-2 py-1.5 hover:bg-gray-100 rounded-lg text-xs text-gray-500 flex items-center gap-1" title="Preview">
                    <Bell size={14} className="text-gray-500" />
                    <span>Play</span>
                  </button>
                </div>
              ))}
              <button onClick={() => notifInputRef.current?.click()}
                className="w-full text-left px-3 py-2 rounded-lg text-sm text-blue-600 hover:bg-blue-50">
                Upload custom tone...
              </button>
              {selectedTune === 'custom' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <span className="text-green-600 text-lg">&#10003;</span>
                  <span className="text-sm text-green-700 flex-1">Custom tone active</span>
                  <button onClick={() => {
                    const url = localStorage.getItem('dws_customer_tune_url');
                    if (url) {
                      const audio = new Audio(url);
                      audio.volume = 0.5;
                      audio.play().catch(() => toast.error('Cannot play audio'));
                    }
                  }} className="px-2 py-1 text-xs text-green-600 hover:bg-green-100 rounded flex items-center gap-1">
                    <Bell size={14} />
                    <span>Play</span>
                  </button>
                </div>
              )}
              <input ref={notifInputRef} type="file" accept="audio/*" className="hidden"
                onChange={(e) => { 
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      localStorage.setItem('dws_customer_tune', 'custom');
                      localStorage.setItem('dws_customer_tune_url', ev.target.result);
                      setSelectedTune('custom');
                      setTuneSaved(true);
                      setTimeout(() => setTuneSaved(false), 3000);
                      toast.success('Custom tone set!');
                    };
                    reader.readAsDataURL(file);
                  }
                }} />
            </div>
          )}
        </div>

        {/* Update Password */}
        <div className="bg-white mt-2 px-5 py-4">
          <button onClick={() => setShowChangePassword(!showChangePassword)} className="w-full flex items-center gap-3 py-2 text-[#008069] hover:text-[#006e5a]">
            <Lock size={20} />
            <span className="text-sm font-medium">Update Password</span>
          </button>
          {showChangePassword && (
            <div className="mt-3 space-y-2">
              <input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
                placeholder="Current Password" className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]" />
              <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                placeholder="New Password (min 6 chars)" className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]" />
              <button onClick={async () => {
                if (!currentPwd || !newPwd) { toast.error('Fill both fields'); return; }
                if (newPwd.length < 6) { toast.error('Min 6 characters'); return; }
                setChangingPwd(true);
                try {
                  await axios.post(`${API}/auth/change-password`, { user_id: user.id, current_password: currentPwd, new_password: newPwd });
                  toast.success('Password updated!');
                  setShowChangePassword(false); setCurrentPwd(''); setNewPwd('');
                } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
                setChangingPwd(false);
              }} disabled={changingPwd}
                className="w-full py-2 bg-[#008069] text-white rounded-lg text-sm font-medium hover:bg-[#006e5a] disabled:opacity-50"
                data-testid="update-password-btn">
                {changingPwd ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          )}
        </div>

        {/* Sign Out */}
        <div className="bg-white mt-2 px-5 py-4 mb-8">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 py-2 text-red-500 hover:text-red-700">
            <SignOut size={20} />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </div>
      </div>

      {/* P22 — resize handle (bottom-right corner) */}
      <div
        onMouseDown={(e) => { e.stopPropagation(); beginDrag(e, 'resize'); }}
        onTouchStart={(e) => { e.stopPropagation(); beginDrag(e, 'resize'); }}
        className="absolute w-6 h-6 cursor-se-resize z-10"
        style={{ left: box.x + box.w - 24, top: box.y + box.h - 24, background: 'linear-gradient(135deg, transparent 45%, #64748b 45%, #64748b 55%, transparent 55%, transparent 70%, #64748b 70%, #64748b 80%, transparent 80%)' }}
        data-testid="dashboard-resize-handle"
      />

      {/* Bank Form Modal */}
      {showBankForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBankForm(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Bank Details</h3>
              <button onClick={() => setShowBankForm(false)} className="p-1 hover:bg-gray-100 rounded-full"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              {[
                { key: 'bank_name', label: 'Bank Name', placeholder: 'HDFC Bank' },
                { key: 'account_holder_name', label: 'A/c Holder Name', placeholder: 'Full name' },
                { key: 'bank_account_number', label: 'Bank Account No', placeholder: '1234567890' },
                { key: 'ifsc_code', label: 'IFSC Code', placeholder: 'HDFC0001234' },
                { key: 'upi_id', label: 'UPI ID', placeholder: 'name@upi' },
                { key: 'address', label: 'Address', placeholder: 'City, State' },
              ].map(field => (
                <div key={field.key}>
                  <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
                  <input value={bankData[field.key] || ''} onChange={e => setBankData({ ...bankData, [field.key]: e.target.value })}
                    placeholder={field.placeholder} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#008069] outline-none" />
                </div>
              ))}
            </div>
            <button onClick={handleSaveBank} disabled={savingBank}
              className="w-full mt-4 py-3 bg-[#008069] text-white rounded-lg font-semibold hover:bg-[#006e5a] disabled:opacity-50">
              {savingBank ? 'Saving...' : 'Save Bank Details'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
